import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { usePostHog } from 'posthog-js/react';

// Create a Context for the Disambiguation
const AppContext = createContext();


// Create a Provider component
export const AppProvider = ({ children }) => {
  const { user, getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0();

  const [skipDisambiguation, setSkipDisambiguation] = useState(() => {
    try {
      const storedValue = localStorage.getItem('skipDisambiguation');
      return storedValue !== null ? JSON.parse(storedValue) : true;
    } catch (error) {
      console.error('Error reading skipDisambiguation from localStorage:', error);
      return true;
    }
  });

  // State to control whether to show reward popup
  const [showRewardPopup, setShowRewardPopup] = useState(() => {
    try {
      // First check if user has permanently disabled the popup
      const permanentlyDisabled = localStorage.getItem('permanentlyDisableRewardPopup');
      if (permanentlyDisabled === 'true') {
        return false;
      }
      
      // Otherwise use the regular toggle state
      const storedValue = localStorage.getItem('showRewardPopup');
      return storedValue !== null ? JSON.parse(storedValue) : true;
    } catch (error) {
      console.error('Error reading reward popup settings from localStorage:', error);
      return true;
    }
  });

  // Save showRewardPopup state to localStorage with user decision persistence
  useEffect(() => {
    localStorage.setItem('showRewardPopup', JSON.stringify(showRewardPopup));
    
    // If user has explicitly set this to false (via Don't Show Again), 
    // we want to record that as a permanent choice
    if (showRewardPopup === false) {
      try {
        localStorage.setItem('permanentlyDisableRewardPopup', 'true');
      } catch (error) {
        console.error('Error saving permanent popup preference:', error);
      }
    }
  }, [showRewardPopup]);

  useEffect(() => {
      localStorage.setItem('skipDisambiguation', JSON.stringify(skipDisambiguation));
  }, [skipDisambiguation]);

  useEffect(() => {
    try {
      const storedValue = localStorage.getItem('skipDisambiguation');
      if (storedValue !== null) {
        setSkipDisambiguation(JSON.parse(storedValue));
      }
    } catch (error) {
      // console.error('Failed to retrieve skipDisambiguation from localStorage:', error);
      // Optionally, you can reset to default if parsing fails
      setSkipDisambiguation(false);
    }
  }, []); // Empty dependency array ensures this runs once on mount

  // Function to toggle showRewardPopup state
  const toggleShowRewardPopup = () => {
    setShowRewardPopup(prev => !prev);
  };

  // Function to reset the permanent disable preference
  const resetRewardPopupPreference = () => {
    try {
      localStorage.removeItem('permanentlyDisableRewardPopup');
      setShowRewardPopup(true);
    } catch (error) {
      console.error('Error resetting reward popup preference:', error);
    }
  };

  const [version, setVersion] = useState("pro")
  const [isSearchMoved, setIsSearchMoved] = useState(false);
  const [mode, setMode] = useState("verify")
  const [errorDisplay, setErrorDisplay] = useState("")
  const [email, setEmail] = useState(null);

  const [overlayLogin, setOverlayLogin] = useState(false)
  const [seekto, setSeekto] = useState(0)
  
  const [ids, setIds] = useState([])
  const [currentConversation, setCurrentConversation] = useState("")
  const [queries, setQueries] = useState([])
  const [conversations, setConversations] = useState([])
  const [link, setLink] = useState("")
  const [NewSearch, setNewSearch] = useState(true)
  const [vismode, setVismode] = useState({})

  const [workspaceLoading, setWorkspaceLoading] = useState(false)
  const [hideSearchBar, setHideSearchBar] = useState(false)
  const [idHistory, setIdHistory] = useState({})
  const [progress, setProgress] = useState({})

  const [userLocCity, setUserLocCity] = useState("")
  const [userLocCtry, setUserLocCtry] = useState("")
  const [userLocReg, setUserLocReg] = useState("")
  const [headlines, setHeadlines] = useState([])
  const [sourceFindMode, setSourceFindMode] = useState(false)
  const [userCredits, setUserCredits] = useState(0)
  const [totalUserCredits, setTotalUserCredits] = useState(0)
  const [dailyUserCredits, setDailyUserCredits] = useState(0)
  const [totalTemporaryUserCredits, setTotalTemporaryUserCredits] = useState(0)
  const [temporaryUserCreditsList, setTemporaryUserCreditsList] = useState([])
  const [temporaryExpiries,setTemporaryExpiries] = useState([])
  const [lifetimeUserCredits, setLifetimeUserCredits] = useState(0)

  const [creditsLoading, setCreditsLoading] = useState(true)
  const [claimsRecieved, setClaimsRecieved] = useState(false)
  const [hasSeenTut, setHasSeenTut] = useState(false)
  const [run, setRun] = useState(false);
  const [forceRun, setForceRun] = useState(false);


  const [city, setCity] = useState('')
  const [country, setCountry] = useState('')
  const posthog = usePostHog();


  // const backendUrl = 'https://backend-word-testing-934923488639.us-central1.run.app';

  const backendUrl = 'https://rdjinpnweu.us-west-2.awsapprunner.com';
  // const backendUrl = 'https://rdjinpnweu.us-west-2.awsapprunner.com'
  
  // const claimExtractUrl = 'http://localhost:5001';


  // const backendUrl = 'https://backend.facticity.ai';
  // const backendUrl = 'http://localhost:5000'; 

  const claimExtractUrl = 'https://backend.facticity.ai';
  const [searchQuery, setSearchQuery] = useState("");

  const [isProUser, setIsProUser] = useState("basic"); // basic subscription tier as default
  
  const [seektrigger, setSeekTrigger] = useState(true)
  const [highlightSearch, setHighlightSearch] = useState(false); // New state

  // Add state for discover posts
  const [discoverPosts, setDiscoverPosts] = useState([]);
  
  // Fetch discover posts
  useEffect(() => {
    const fetchDiscoverPosts = async () => {
      try {
        // Updated to use the same API endpoint as Discover.js with appropriate params
        const userParam = (isAuthenticated && user?.email) ? `&user_email=${encodeURIComponent(user.email)}` : '';
        const response = await fetch(
          `${backendUrl}/api/published-posts?skip=0&limit=5&sort_by=recent${userParam}&tab=feed`
        );
        
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        const data = await response.json();
        setDiscoverPosts(data || []);
      } catch (error) {
        console.error("Error fetching discover posts:", error);
      }
    };
    
    fetchDiscoverPosts();
    
    // Set up a refresh interval for discover posts (every 5 minutes)
    const intervalId = setInterval(fetchDiscoverPosts, 5 * 60 * 1000);
    
    return () => clearInterval(intervalId);
  }, [backendUrl, isAuthenticated, user?.email]);

  // useEffect(() => {
  //   if (posthog) {
  //     console.log("PostHog instance received in AppProvider");
  //     console.log(posthog)

  //     posthog.capture('$pageview', {
  //       $set: { page_viewed: true }
  //     })

  //     // Wait for person properties to be set
  //     const unsubscribe = posthog.onFeatureFlags(() => {
  //       const countryName = posthog.getFeatureFlag('$geoip_country_name')
  //       console.log('geoip country name')
  //       console.log(countryName)
  //       setCountry(countryName)
  //       const cityName = posthog.getFeatureFlag('$geoip_city_name')
  //       console.log('geoip city name')
  //       console.log(cityName)
  //       setCity(cityName || '')
  //       console.log(cityName)
  //       //setCountry(posthog.getFeatureFlag('$geoip_country_name') || '')
  //     })

  //     return () => unsubscribe()
  //   }
  // }, [posthog])

  // useEffect(() => {
  //   if (posthog) {
  //     console.log("PostHog instance received in AppProvider");
      
  //     // Capture pageview
  //     posthog.capture('$pageview');
      
  //     // Try to access data through persistence
  //     if (posthog.persistence && posthog.persistence.properties) {
  //       console.log("Persistence properties:", posthog.persistence.properties());
        
  //       // Check if geo data is in persistence
  //       const props = posthog.persistence.properties();
  //       const countryName = props.$geoip_country_name;
  //       const cityName = props.$geoip_city_name;
        
  //       console.log('Country from persistence:', countryName);
  //       console.log('City from persistence:', cityName);
        
  //       if (countryName) setCountry(countryName);
  //       if (cityName) setCity(cityName);
  //     }
      
  //     // Try session props if available
  //     if (posthog.sessionPropsManager) {
  //       console.log("Session properties:", posthog.sessionPropsManager.getProperties());
        
  //       // Check if geo data is in session props
  //       const sessionProps = posthog.sessionPropsManager.getProperties();
  //       if (sessionProps.$geoip_country_name) setCountry(sessionProps.$geoip_country_name);
  //       if (sessionProps.$geoip_city_name) setCity(sessionProps.$geoip_city_name);
  //     }
  //   }
  // }, [posthog]);
  
  // useEffect(() => {
  //   if (posthog) {
  //     console.log("PostHog instance received in AppProvider");
      
  //     // Get the distinct_id to pass to your backend
  //     const distinctId = posthog.get_distinct_id();
      
  //     // Call your Flask backend endpoint
  //     fetch(`http://localhost:5000/user-location?distinct_id=${distinctId}`)
  //       .then(response => response.json())
  //       .then(data => {
  //         console.log('Location data from backend:', data);
          
  //         if (data.country) setCountry(data.country);
  //         if (data.city) setCity(data.city);
  //       })
  //       .catch(error => {
  //         console.error('Error fetching location from backend:', error);
  //       });
  //   }
  // }, [posthog]);
  
  
  


  useEffect(() => {
    if (isAuthenticated) {
      setEmail(user?.email || "");
      setOverlayLogin(false);
    } else {
      setEmail("");
    }
  }, [isAuthenticated, user]);

  const fetchSubscriptionStatus = async () => {
    setCreditsLoading(true)
    if (isAuthenticated && user?.email) {
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(`${backendUrl}/get_user_subscription_by_email`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ email: user.email }),
        });

        if (response.ok) {
          const data = await response.json();
          if (data.subscriptions && data.subscriptions.some(sub => sub.status === "active" || sub.status === "trialing")) {
            setIsProUser(true);
          } else {
            setIsProUser(false);
          }
        } else {
          console.error("Failed to fetch subscription status:", response.statusText);
        }
      } catch (error) {
        console.error("Error fetching subscription status:", error);
      }

      try {
        //const backendUrl = "http://localhost:5000"
        const response = await fetch(`${backendUrl}/check_credits_util`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            //"Authorization" : `Bearer ${token}`,
          },
          body: JSON.stringify({userEmail: user.email})
        });

        if(response.ok)
        {
          const data = await response.json()
          if(data.user_credits){
            console.log(data.user_credits)
            setTotalUserCredits(data.user_credits)
            setUserCredits(data.user_credits)
          }

          if(data.daily_credits){
            console.log(data.daily_credits)
            setDailyUserCredits(data.daily_credits)
          }

          if(data.temp_total){
            console.log(data.temp_total)
            setTotalTemporaryUserCredits(data.temp_total)
          }

          if(data.temp_list){
            console.log(data.temp_list)
            setTemporaryUserCreditsList(data.temp_list)
          }

          if(data.temp_expiries){
            console.log(data.temp_expiries)
            setTemporaryExpiries(data.temp_expiries)
          }

          if(data.lifetime_credits){
            console.log(data.lifetime_credits)
            setLifetimeUserCredits(data.lifetime_credits)
          }

          setCreditsLoading(false)
          
        }

      } catch (error) {
        console.error("Error fetching user credits: ", error)
      }
    }
    setCreditsLoading(false)
  };

  useEffect(() => {
   

    fetchSubscriptionStatus();
  }, [isAuthenticated, user, getAccessTokenSilently]);



  useEffect(() => {
    const fetchConversations = async () => {
      try {
        const response = await fetch(backendUrl+'/update_ids', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: email, // replace with actual email
            id: "" // empty id
          })
        });

        if (!response.ok) {
          // console.log("Network response was not ok");
        } else{
            const data = await response.json();
            setConversations(data.ids);
        }


        // setCurrentConversation(data.ids[0]); // Set the first conversation as the current one
      } catch (error) {
        // console.error("Error fetching conversations:", error);
      }
    };

    fetchConversations();
  }, [email]);

  useEffect(() => {
   
    const getHasSeenTutFlag = async () => {
      try {
        const response = await fetch(backendUrl + '/has-seen-tut', {
          method: 'POST', 
          headers: { 
            'Content-Type' : 'application/json'
          },
          body : JSON.stringify({
            userEmail : user?.email || null
          })
        });

        if(!response.ok){
          //
        }
        else {
          const data = await response.json();
            setHasSeenTut(data.hasSeenTut);
        }
      }
      catch {
        //
      }
    };
    if(isAuthenticated) {
    getHasSeenTutFlag();
    }
  }, [email])

  const fetchTaskStatus = async (conversationId) => {
    // console.log("FETCHING", conversationId)
    let data = null
    try {
      setWorkspaceLoading(true);
      setIsSearchMoved(true);
      // const response = await fetch('https://backend-word-testing-934923488639.us-central1.run.app/check-multiple-task-status'
      const response = await fetch(backendUrl+'/check-multiple-task-status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ _id: conversationId }), // assuming the conversationId matches the "_id" expected by the endpoint
      });
    
      // console.log({fetchStatus:response})

  
      data = await response.json();
      // console.log({ data });

      // if(data && data.taskScheduled){
      //   console.log(data.progress);
      //   setMode(data.vis_mode.mode);
      //   setLink(data.vis_mode.link);
      //   setProgress(data.progress);
      //   return;
      // }

      if (!response.ok) {
        setErrorDisplay("Session ended abruptly, no conversation found.")
        console.warn(`Backend responded with status: ${response.status}`);
        return; // Exit early if the response is not OK
      }

      if (!data.responses || data.responses.length === 0) {
        // console.log("No responses found");
        data.responses = [{}]
        // return; // Exit early if there are no responses
      }
      // setVismode(data.vis_mode)
      setClaimsRecieved(true)
      const mappedResponses = data.responses.reduce((acc, response) => {
        acc[response.task_id] = response;
        return acc;
      }, {});
  
      console.log({mappedResponses})
      setIdHistory(mappedResponses);
      setIds(Object.keys(mappedResponses));
      setQueries(Object.values(mappedResponses).map((response) => response.input));

      setMode(data.vis_mode.mode)
      setLink(data.vis_mode.link)
      setProgress(data.progress)


      window.history.replaceState(null, '', `/c/${conversationId}`);
    } catch (error) {
      console.warn('An error occurred while fetching task status:', error);
      // Log the error and silently handle it without updating state
    } finally {
      setWorkspaceLoading(false);
    }
  };
  

  useEffect(() => {
    // console.log({ NewSearch });
    if (!isAuthenticated && mode =="extractFacts"){
      setHideSearchBar(true)
    }
    // console.log(isAuthenticated,queries.length)
    if (!isAuthenticated && currentConversation){
      setHideSearchBar(true)
    }
    if (currentConversation !== undefined && currentConversation !== "" && !NewSearch) {
      // console.log("excecuted",NewSearch)
      fetchTaskStatus(currentConversation);
    }

  }, [currentConversation, conversations, NewSearch, vismode, mode]);

  const fetchUserLocation = async () => {
    try {
      const res = await fetch("https://ipinfo.io/json?token=5555ba99952ea6");
      const data = await res.json();
      console.log(data.country)
      console.log(data.city)
      return { country: data.country, city: data.city || data.region };
      
      //console.log(data) 
    } catch (error) {
      console.error("Error fetching loc info:", error);
      return null;
    }
  };
  
  const fetchCountryNews = async (country) => {
    try {
      //const backendUrl = 'http://localhost:5000';
      const res = await fetch(backendUrl + `/news/country?country=${country}`);
      const data = await res.json();
  
      // Ensure you're returning an array of article objects
      const articles = data.headlines || [];
  
      console.log(articles); // This will be an array of article objects
      return articles;
    } catch (error) {
      console.error("Error fetching country news:", error);
      return [];
    }
  };
  

  useEffect(() => {
    const loadHeadlines = async () => {
      const { country, city } = await fetchUserLocation();
      const countryNews = await fetchCountryNews(country)
      console.log(countryNews)
      setUserLocCtry(country);
      setUserLocCity(city);
      setHeadlines(countryNews);
    };
  
    loadHeadlines();
  }, []);
  
  // Function to toggle skipDisambiguation state
  const toggleSkipDisambiguation = () => {
    setSkipDisambiguation(prev => !prev);
  };

  return (
    <AppContext.Provider value={{ 
        skipDisambiguation, 
        setSkipDisambiguation, 
        toggleSkipDisambiguation,
        showRewardPopup,
        setShowRewardPopup,
        toggleShowRewardPopup,
        resetRewardPopupPreference,
        version,
        setVersion,
        currentConversation,
        setCurrentConversation,
        queries,
        setQueries,
        ids,
        setIds,
        conversations,
        setConversations,
        idHistory, 
        setIdHistory,
        isSearchMoved, 
        setIsSearchMoved,
        workspaceLoading,
        mode,
        setMode,
        link, setLink, errorDisplay, setErrorDisplay, email, setNewSearch,
        overlayLogin, setOverlayLogin, hideSearchBar, setHideSearchBar, sourceFindMode, setSourceFindMode,
        isProUser, setIsProUser, seekto, setSeekto, setSeekTrigger, seektrigger, claimExtractUrl, backendUrl, userLocCity, userLocReg, userLocCtry, headlines,
        claimsRecieved, setClaimsRecieved,
        creditsLoading, setCreditsLoading,
        userCredits, setUserCredits,
        searchQuery, setSearchQuery,
        highlightSearch, setHighlightSearch, progress, setProgress,
        hasSeenTut,
        run, setRun,
        forceRun, setForceRun,
        totalUserCredits, setTotalUserCredits,
        dailyUserCredits, setDailyUserCredits,
        temporaryUserCreditsList, setTemporaryUserCreditsList,
        lifetimeUserCredits, setLifetimeUserCredits,
        temporaryExpiries, setTemporaryExpiries,
        totalTemporaryUserCredits, setTotalTemporaryUserCredits,
        fetchSubscriptionStatus,
        discoverPosts, setDiscoverPosts
        }}>
      {children}
    </AppContext.Provider>
  );
};

// Custom hook to use the AppContext
export const useAppContext = () => {
  return useContext(AppContext);
};
